import React, { Dispatch, SetStateAction, useState } from 'react';
import {
  View, StyleSheet, Text, TextInput, FlatList, Pressable, Platform,
} from 'react-native';
import SearchIcon16 from '../../../../../components/16pxIcons/SearchIcon16';
import PressableStyled, { PressableState } from '../../../../../components/PressableStyled';
import EllipsisIcon from '../../../../../components/EllipsisIcon';
import PlusIcon from '../../../../../components/16pxIcons/PlusIcon';
import Colors from '../../../../../constants/Colors';
import Style from '../../../../../constants/Style';
import { TemplateGroup } from '../../../../../constants/Types';
import Button from '../../../../../components/Button';
import SelectionModal from '../../../../../components/SelectionModal';

interface ModalContentProps {
  templateGroups: TemplateGroup[],
  setSelectedTemplateGroup: Dispatch<SetStateAction<TemplateGroup | null>>,
  hideTemplateGroupSelection: () => void,
  onAddTemplateGroup: () => void,
  canAddTemplateGroup: boolean,
  canEditTemplateGroup: boolean,
  canDeleteTemplateGroup: boolean,
  // eslint-disable-next-line no-unused-vars
  onEditTemplateGroup: (templateGroup: TemplateGroup) => void,
  // eslint-disable-next-line no-unused-vars
  onDeleteTemplateGroup: (templateGroup: TemplateGroup) => void,
}

type OptionActionType = 'edit' | 'delete';

interface PendingOptionAction {
  type: OptionActionType,
  templateGroup: TemplateGroup,
}

const ModalContent = ({
  templateGroups,
  setSelectedTemplateGroup,
  hideTemplateGroupSelection,
  onAddTemplateGroup,
  canAddTemplateGroup,
  canEditTemplateGroup,
  canDeleteTemplateGroup,
  onEditTemplateGroup,
  onDeleteTemplateGroup,

}: ModalContentProps) => {
  const [templateGroupSearchValue, setTemplateGroupSearchValue] = useState<string>('');
  const [optionsTemplateGroup, setOptionsTemplateGroup] = useState<TemplateGroup | null>(null);
  const [pendingOptionAction, setPendingOptionAction] = useState<PendingOptionAction | null>(null);
  const templateGroupListData: TemplateGroup[] = templateGroups
    ?.filter((templateGroup: TemplateGroup) => templateGroup
      .Name.toLowerCase().includes(templateGroupSearchValue.trim().toLowerCase()));

  const selectTemplateGroup = (templateGroup: TemplateGroup) => {
    setSelectedTemplateGroup(templateGroup);
    hideTemplateGroupSelection();
  };

  const hideOptions: () => void = () => {
    setOptionsTemplateGroup(null);
  };

  const addTemplateGroup: () => void = () => onAddTemplateGroup();

  const editTemplateGroup = (templateGroup: TemplateGroup) => {
    setPendingOptionAction({ type: 'edit', templateGroup });
    hideOptions();
  };

  const promptDeleteGroup = (templateGroup: TemplateGroup) => {
    setPendingOptionAction({ type: 'delete', templateGroup });
    hideOptions();
  };

  const onOptionsModalHide: () => void = () => {
    if (!pendingOptionAction?.templateGroup) return;

    if (pendingOptionAction.type === 'edit') {
      onEditTemplateGroup(pendingOptionAction.templateGroup);
    } else {
      onDeleteTemplateGroup(pendingOptionAction.templateGroup);
    }

    setPendingOptionAction(null);
  };

  return <View style={{ height: '100%' }}>
    <View style={styles.searchContainer}>
      <TextInput
        style={styles.searchBar}
        placeholder='Search template groups'
        value={templateGroupSearchValue}
        onChangeText={setTemplateGroupSearchValue}
      />
      <SearchIcon16 style={styles.searchIcon} />
    </View>
    <FlatList
      nestedScrollEnabled
      style={styles.templateGroupList}
      data={[null, ...(templateGroupListData ?? [])]}
      keyExtractor={(templateGroup: TemplateGroup | null) => `template_group_${templateGroup
        ?.ButtonPrototypeGroupId}`}
      renderItem={({ item: templateGroup }) => <PressableStyled
        style={styles.templateGroupItem}
        onPress={() => selectTemplateGroup(templateGroup)}
      >
        <View>
          <Text style={styles.templateGroupName} numberOfLines={1} ellipsizeMode='tail'>
            {templateGroup ? templateGroup.Name : 'All templates'}
          </Text>
          {!!templateGroup && <Text style={styles.templateGroupCountLabel}>
            {`${templateGroup.buttonPrototypes.length} template${templateGroup.buttonPrototypes.length === 1 ? '' : 's'}`}
          </Text>}
        </View>
        {!!templateGroup && <PressableStyled
          onPress={() => setOptionsTemplateGroup(templateGroup)}
          style={{
            position: 'absolute', right: 0, top: 0, bottom: 0, justifyContent: 'center', paddingHorizontal: 12,
          }}
        >
          <EllipsisIcon />
        </PressableStyled>}
      </PressableStyled>}
    />
    {canAddTemplateGroup && <Button
      title='Add template group'
      children={<PlusIcon style={styles.plusIcon} />}
      affirmative
      style={styles.addTemplateGroupButton}
      onPress={addTemplateGroup}
    />}
    <SelectionModal
      isVisible={!!optionsTemplateGroup}
      hide={hideOptions}
      onModalHide={onOptionsModalHide}
      title={optionsTemplateGroup?.Name ?? 'Select template group'}
      listContent={<>
        {canEditTemplateGroup && <Pressable
          style={({ hovered, pressed }: PressableState) => [
            styles.option, hovered && Style.listItemPressed, pressed && Style.listItemPressed,
          ]}
          onPress={() => {
            if (!optionsTemplateGroup) return;
            editTemplateGroup(optionsTemplateGroup);
          }}
        >
          <Text style={{ color: Colors.light.hereworksBlack500 }}>{'Edit'}</Text>
        </Pressable>}
        {canDeleteTemplateGroup && <Pressable
          style={({ hovered, pressed }: PressableState) => [
            styles.option, hovered && Style.listItemPressed, pressed && Style.listItemPressed,
          ]}
          onPress={() => {
            if (!optionsTemplateGroup) return;
            promptDeleteGroup(optionsTemplateGroup);
          }}
        >
          <Text style={{ color: Colors.light.hereworksBlack500 }}>{'Delete'}</Text>
        </Pressable>}
      </>}
    />
  </View>;
};

const styles = StyleSheet.create({
  addTemplateGroupButton: {
    flexShrink: 0,
    flex: undefined,
    flexDirection: 'row-reverse',
    height: 32,
    marginBottom: Platform.OS === 'android' ? 40 : 0,
  },
  option: {
    paddingVertical: 8,
    paddingHorizontal: 24,
    marginHorizontal: -24,
  },
  plusIcon: {
    marginEnd: 8,
  },
  searchBar: {
    paddingStart: 36,
    paddingEnd: 12,
    paddingVertical: 8,
    borderWidth: 1.5,
    borderRadius: 4,
    borderColor: Colors.light.hereworksBlue50,
    marginVertical: 8,
  },
  searchContainer: {
    justifyContent: 'center',
  },
  searchIcon: {
    position: 'absolute',
    start: 12,
  },
  templateGroupCountLabel: {
    fontSize: 12,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
    letterSpacing: 0,
    marginTop: 4,
  },
  templateGroupItem: {
    ...Style.row,
    marginTop: 8,
    paddingHorizontal: 16,
    paddingVertical: 4,
    borderRadius: 8,
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksBlue100,
    flex: 1,
    height: 46,
  },
  templateGroupName: {
    fontSize: 14,
    fontFamily: 'Poppins_400Regular',
    letterSpacing: 0,
    color: Colors.light.hereworksBlack500,
    marginEnd: 16,
  },
  templateGroupList: {
    marginHorizontal: -20,
    paddingHorizontal: 20,
    marginBottom: 16,
    flex: 1,
  },
});

export default ModalContent;
